import * as FullStory from '@fullstory/browser';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { getConversationDateString } from '~components/business-owner/messenger/utils';
import { BetaTagTooltip } from '~components/commons/beta/BetaTagTooltip';

import { useSearchBarryConversationsForUserAndBusiness } from '~api/common/barry/queries/useSearchBarryConversationsForUserAndBusiness';
import { CloseIcon, PurpleBarryIcon, SearchIcon } from '~assets/images';
import { FULL_STORY_BARRY_TRACKING_EVENTS } from '~constants/fullstory/events/barry';
import { useBarry } from '~contexts/BarryContext';
import { GlobalState } from '~reducers';

import styles from './BarrySideNavigation.module.scss';

interface AbridgedConversation {
	id: string;
	title: string;
	createdAt: Date;
}

export interface BarrySideNavigationProps {
	conversations: AbridgedConversation[];
	onToggleSideNavigation: () => void;
}

export const BarrySideNavigation = (props: BarrySideNavigationProps) => {
	const { conversations, onToggleSideNavigation } = props;
	const { openWithConversation, startNewChat } = useBarry();
	const { userId, email, business } = useSelector(
		(state: GlobalState) => state?.session,
	);
	const [searchTerm, setSearchTerm] = useState<string>('');
	const [conversationsToDisplay, setConversationsToDisplay] =
		useState<AbridgedConversation[]>(conversations);

	const { data: searchResults, refetch: searchConversations } =
		useSearchBarryConversationsForUserAndBusiness(searchTerm, !!searchTerm);

	useEffect(() => {
		if (searchResults) {
			const mappedResults = searchResults.conversations.map((c) => ({
				id: c.id,
				title: c.title ?? 'Conversation with Barry',
				createdAt: new Date(c.createdAt),
			}));
			setConversationsToDisplay(mappedResults);
		} else {
			setConversationsToDisplay(conversations);
		}
	}, [conversations, searchResults]);

	const selectConversation = (id: string) => {
		openWithConversation(id);
		onToggleSideNavigation();
	};

	const navigateToNewChat = () => {
		startNewChat();
		onToggleSideNavigation();
		FullStory.event(FULL_STORY_BARRY_TRACKING_EVENTS.barryNavigateToNewChat, {
			userId,
			email,
			business,
		});
	};

	const handleConversationSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
		const query = event.target.value.toLowerCase();
		setSearchTerm(query);
		searchConversations();
	};

	return (
		<div className={styles.barrySideNavContainer}>
			<div className={styles.barrySideNavHeader}>
				<div className={styles.headerLeft}>
					<div className={styles.logoIcon}>
						<PurpleBarryIcon />
					</div>
					<h1 className={styles.appTitle}>Barry</h1>
					<BetaTagTooltip placement="right" />
				</div>
				<div className={styles.headerRight} onClick={onToggleSideNavigation}>
					<button className={styles.closeButton}>
						<CloseIcon />
					</button>
				</div>
			</div>

			<div className={styles.searchSection}>
				<div className={styles.searchContainer}>
					<div className={styles.searchIcon}>
						<SearchIcon />
					</div>
					<input
						type="text"
						placeholder="Search..."
						className={styles.searchInput}
						onChange={handleConversationSearch}
					/>
				</div>
				<button className={styles.newChatButton} onClick={navigateToNewChat}>
					<div className={styles.newChatIcon}>
						<PurpleBarryIcon />
					</div>
					New Chat
				</button>
			</div>

			<div className={styles.chatHistory}>
				{conversationsToDisplay.map((chat, index) => (
					<div
						key={chat.id}
						onClick={() => {
							selectConversation(chat.id);
						}}
					>
						<div className={styles.chatItem}>
							<span className={styles.chatMessage}>{chat.title}</span>
							<span className={styles.chatMessage}>
								{getConversationDateString(chat.createdAt.toString())}
							</span>
						</div>
						{index < conversations.length - 1 && (
							<div className={styles.chatDivider}></div>
						)}
					</div>
				))}
			</div>
		</div>
	);
};
